<template>
  <div class="tc-video-player-box" :style="{ transform: `scale(${autoScale ? screenScale : 1})` }">
    <!-- Loading overlay: replaces only .video-wrapper slot when player not ready -->
    <div class="video-wrapper" v-show="!isMatchDayEnding && streamUrl && !videoPlayerReady">
      <VideoLoadingOverlay variant="placeholder" />
    </div>
    <div class="video-wrapper" v-show="!isMatchDayEnding && streamUrl && videoPlayerReady" ref="videoPlayerContainer">
      <div :key="videoPlayerKey" class="video-player-key-wrapper">
        <video
          :id="playerDomId"
          webkit-playsinline="true"
          x-webkit-airplay="true"
          playsinline
          allowfullscreen="true"
          webkitallowfullscreen="true"
          mozallowfullscreen="true"
          x5-video-player-type="h5"
          x5-video-player-fullscreen="true"
          @play="onPlayerPlay($event)"
          @pause="onPlayerPause($event)"
          @ended="onPlayerEnded($event)"
          @loadeddata="onPlayerLoadeddata($event)"
          @waiting="onPlayerWaiting($event)"
          @playing="onPlayerPlaying($event)"
          @timeupdate="onPlayerTimeupdate($event)"
          @canplay="onPlayerCanplay($event)"
          @canplaythrough="onPlayerCanplaythrough($event)"
          @statechanged="playerStateChanged($event)"
          :options="playerOptions"
          :class="{ isLarger: screenScale > 1 }"
        />
      </div>

      <!-- Text Watermark (Type 1) -->
      <div
        v-if="
          watermarkVisible &&
          streamWatermarkData &&
          streamWatermarkData.watermarkType === 1 &&
          streamWatermarkData.watermark
        "
        class="video-layers"
        :class="`movement-${watermarkMovementType}`"
        :style="watermarkStyle"
      >
        {{ streamWatermarkData.watermark }}
      </div>

      <!-- Image Watermark (Type 2) -->
      <img
        v-if="
          watermarkVisible &&
          streamWatermarkData &&
          streamWatermarkData.watermarkType === 2 &&
          streamWatermarkData.watermarkImageUrl
        "
        :src="getWatermarkImageUrl()"
        class="video-layers video-layers-image"
        :class="`movement-${watermarkMovementType}`"
        :style="watermarkStyle"
        alt="watermark"
      />

      <!-- Text Watermark with configurable color & opacity (Type 5) -->
      <div
        v-if="
          watermarkVisible &&
          streamWatermarkData &&
          streamWatermarkData.watermarkType === 5 &&
          streamWatermarkData.watermark
        "
        class="video-layers"
        :class="`movement-${watermarkMovementType}`"
        :style="watermarkType5Style"
      >
        {{ streamWatermarkData.watermark }}
      </div>

      <!-- Text Watermark (Type 4) - both text AND robust watermark -->
      <div
        v-if="
          watermarkVisible &&
          streamWatermarkData &&
          streamWatermarkData.watermarkType === 4 &&
          streamWatermarkData.watermark
        "
        class="video-layers"
        :class="`movement-${watermarkMovementType}`"
        :style="watermarkStyle"
      >
        {{ streamWatermarkData.watermark }}
      </div>

      <!-- Robust watermark overlay (Type 3: only robust, Type 4: robust + text) -->
      <div
        v-if="
          streamWatermarkData &&
          (streamWatermarkData.watermarkType === 3 || streamWatermarkData.watermarkType === 4) &&
          streamWatermarkData.watermark
        "
        class="robust-watermark-overlay"
        :style="robustWatermarkOverlayStyle"
      >
        {{ streamWatermarkData.watermark }}
      </div>

      <!-- Pull-to-refresh indicator (mobile: iOS Safari, Chrome Android) -->
      <div v-if="isMobile && (isPullingToRefresh || isRefreshingStream)" class="pull-refresh-overlay">
        <div
          class="pull-refresh-icon"
          :class="{
            'is-refreshing': isRefreshingStream,
            'is-ready': !isRefreshingStream && pullDistance >= pullRefreshThreshold,
          }"
          :style="{
            transform: `translateY(${isRefreshingStream ? 0 : Math.min(pullDistance, pullRefreshThreshold * 1.2)}px)`,
            opacity: isRefreshingStream ? 1 : Math.min(pullDistance / pullRefreshThreshold, 1),
          }"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="23 4 23 10 17 10"></polyline>
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
          </svg>
        </div>
      </div>

      <!-- Over the video, e.g. the bet placed / cancelled pill (video-toast.vue). -->
      <slot name="overlay" />
    </div>

    <div v-if="!streamUrl" class="no-active-arena">
      <img class="logo" v-if="!streamError" :src="require('@/assets/images/alert.webp')" />
      <img class="logo-out-of-balance" v-else :src="require('@/assets/images/out-of-money.png')" />
      <template v-if="streamError">
        <div class="text my-3">
          {{ $t('Stream locked') }}
        </div>
        <div class="text">
          {{ convertStreamErrorMessage(streamError) }}
        </div>
      </template>
      <template v-else>
        <div class="text">
          {{ $t('No fight at the moment') }}
        </div>
        <div class="text">
          {{ $t('Please come back again') }}
        </div>
      </template>
    </div>
    <div v-if="isMatchDayEnding" class="no-active-arena match-day-ending">
      <img class="logo" :src="require('@/assets/images/alert.webp')" />
      <div class="text">
          {{ $t('This arena has ended') }}
      </div>
      <div class="text">
          {{ $t('Thank you for your support') }}
      </div>
    </div>
    <div v-if="!embedded" :class="['video-banner-wrapper', { 'is-mobile': isMobile, reverse: isReverse }]">
      <div class="odds-wrapper">
        <span>{{ $t('Banker') }}</span>
        <span v-if="autoScale && screenScale > 1" class="ml-16 odds">{{ bankerOdds }}</span>
      </div>
      <div class="odds-wrapper">
        <span v-if="autoScale && screenScale > 1" class="mr-16 odds">{{ playerOdds }}</span>
        <span>{{ $t('Player') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
// Robust watermark overlay config - matches moderate preset from robust-watermark.ts
const ROBUST_WATERMARK_MODERATE = {
  movementSpeed: 0.5,
  opacity: 0.25,
  opacityVariation: 0.04,
  blurAmount: 6,
  // Interpolation factor: movementSpeed * 0.04 for smooth moderate movement
  interpolationSpeed: 0.02, // 0.5 * 0.04
  // Distance threshold to pick new target (in % space, robust-watermark uses 0.03 in 0-1)
  targetDistanceThreshold: 3,
};

import { mapGetters, mapActions } from 'vuex';
import { reconnectMainConnection } from '@/utils/websocket';
import { FightStatus } from '@/utils/index.model';
import { isMobile, isIOS, isAndroid } from 'mobile-device-detect';
import { find, isEmpty } from 'lodash';
import { getImgUrl, normalizeStreamUrl } from '@/utils/helpers';
import { getBaseUrl, isProdMode } from '@/utils/helpers';
import { AntiPipLayer, createAntiPipLayer } from '@/utils/anti-pip';
import VideoLoadingOverlay from '@/components/video-loading-overlay.vue';

export default {
  components: { VideoLoadingOverlay },
  props: {
    // Set by a desktop pane so this player follows its own arena's stream
    // instead of the single most-recent global one.
    arenaId: {
      type: String,
      default: '',
    },
    /*
     * Rendered inside a desktop pane, where the Banker-vs-Player banner belongs
     * to the pane chrome rather than the player. Matches ve-player's prop of the
     * same name; default false leaves mobile untouched.
     */
    embedded: {
      type: Boolean,
      default: false,
    },
    isAnyPopupOpen: {
      type: Boolean,
      default: false,
    },
    videoPlayerReady: {
      type: Boolean,
      default: false,
    },
    videoPlayerReady: {
      type: Boolean,
      default: false,
    },
    videoPlayerReady: {
      type: Boolean,
      default: false,
    },
    videoPlayerReady: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      // Per instance, so a second pane's player does not reuse (or remove) this
      // one's <video> -- see tc-video.vue.
      playerDomId: `videoPlayerV1Id-${this._uid}`,
      screenScale: 1,
      // arenaBtns: [],
      // if we have bigPlayButton config here, then auto play is not working
      isMobile,
      isIOS,
      tcPlayer: null,
      isPlayerInitializing: false,

      // Anti-PiP layer
      antiPipLayer: null,

      // Pull-to-refresh (iOS)
      pullStartY: 0,
      pullDistance: 0,
      isPullingToRefresh: false,
      isRefreshingStream: false,
      pullRefreshThreshold: 60, // pixels to trigger refresh

      // Watermark frequency control
      watermarkVisible: false,
      watermarkTimer: null,
      watermarkMovementTimer: null, // For continuous movement animation
      watermarkPosition: { x: 0, y: 0, targetX: undefined, targetY: undefined },
      robustWatermarkPosition: { x: 0, y: 0, targetX: undefined, targetY: undefined },
      robustWatermarkMoveTimer: null,
      currentCorner: 'top-left', // For movement type 3: tracks which diagonal path (top or bottom)
      cornerAnimationPhase: 'start', // 'start' or 'end' - tracks position within the diagonal path
      frequencyConfig: {
        0: { mode: 'off' },
        1: { mean_on_s: 2.4, mean_off_s: 21.6 },
        2: { mean_on_s: 2.8, mean_off_s: 11.2 },
        3: { mean_on_s: 3.2, mean_off_s: 7.47 },
        4: { mean_on_s: 3.6, mean_off_s: 5.4 },
        5: { mean_on_s: 4.0, mean_off_s: 4.0 },
        6: { mean_on_s: 4.4, mean_off_s: 2.93 },
        7: { mean_on_s: 4.8, mean_off_s: 2.06 },
        8: { mean_on_s: 5.2, mean_off_s: 1.3 },
        9: { mean_on_s: 5.6, mean_off_s: 0.62 },
        10: { mode: 'always_on' },
      },

      // Stream refresh control
      lastPlayTime: null,
      pauseStartTime: null,
      windowFocusTime: null,
      windowBlurTime: null, // Track when user leaves app
      refreshThreshold: 3000, // 3 seconds threshold
      isReinitializing: false,
      skipNextPlayCheck: false, // Flag to skip reinitialize check after recreation

      // Page visibility tracking (for browser tab/app switch) — same logic as tc-video.vue
      pageHiddenTime: null,
      visibilityThreshold: 1000, // 1 second threshold for reinit on return

      // Error retry control
      retryTimer: null,
      retryCount: 0,
      maxRetries: 36, // 3 minutes total (36 * 5 seconds)
      lastErrorRecoveryTime: 0, // Prevent duplicate recovery for code 4/3
      pendingStreamReinit: false, // Set when waiting for new stream URL
      error4RetryCount: 0, // Retry getArenaStreamUrl for error 4 recovery
      error4RetryTimer: null,

      ensureAutoPlayRetryCount: 0, // Retry autoplay after fullRecreate (iOS)
      // Persisted audio state (survives component remounts)
      savedMuted: null,
      savedVolume: null,

      // iOS autoplay: guard to avoid duplicate play() and listener churn
      autoplaySucceeded: false,

      // iOS volume persist: debounce timer for localStorage writes (mobile perf)
      volumePersistTimer: null,

      // iOS autoplay fallback: single delayed ensureAutoPlay (pull-to-refresh)
      iosAutoplayTimer: null,

      // Android autoplay: muted start + bounded retries for first-load timing.
      androidAutoplayTimer: null,
      androidAutoplayRetryCount: 0,
      androidPlayPending: false,
      androidDesiredMuted: null,
      androidDesiredVolume: null,

      // Force video element re-mount when returning from long background (avoids Error Code 3)
      videoPlayerKey: 0,
    };
  },
  mounted() {
    // Initialize Anti-PiP layer (if pip=false in URL or store)
    this.antiPipLayer = createAntiPipLayer(this.pipDisabled);

    // Restore persisted audio state (if any) so volume preference
    // survives switching between canvas and tcplayer views.
    try {
      const storedMuted = window.localStorage.getItem('tcPlayerMuted');
      const storedVolume = window.localStorage.getItem('tcPlayerVolume');

      this.savedMuted = storedMuted !== null ? storedMuted === 'true' : null;
      this.savedVolume = storedVolume !== null ? parseFloat(storedVolume) : null;
      if (Number.isNaN(this.savedVolume)) {
        this.savedVolume = null;
      }
    } catch (e) {
      this.savedMuted = null;
      this.savedVolume = null;
    }
    this.androidDesiredMuted = this.savedMuted;
    this.androidDesiredVolume = this.savedVolume;

    if (this.arena.id) {
      this.getArenaStreamUrl(this.arena.id);
    }

    // Use minimal delay for first load - just enough for DOM readiness
    // The stream URL is now requested early from cock-fight.vue, so it may already be available
    this.$nextTick(() => {
      this.startPlay();
      this.startWatermarkFrequency();
    });

    // Add window focus/blur listeners
    window.addEventListener('focus', this.onWindowFocus);
    window.addEventListener('blur', this.onWindowBlur);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    // Mobile-specific events (iOS Safari)
    if (isMobile) {
      window.addEventListener('pageshow', this.onPageShow);
      window.addEventListener('pagehide', this.onPageHide);
    }

    // Pull-to-refresh touch listeners (mobile: iOS Safari, Chrome Android)
    if (isMobile) {
      this.$nextTick(() => {
        const container = this.$refs.videoPlayerContainer;
        if (container) {
          container.addEventListener('touchstart', this.onPullTouchStart, { passive: true });
          container.addEventListener('touchmove', this.onPullTouchMove, { passive: false });
          container.addEventListener('touchend', this.onPullTouchEnd, { passive: true });
        }
      });
    }
  },
  computed: {
    ...mapGetters('fight', ['fight']),
    ...mapGetters({
      selectedArena: 'general/selectedArena',
      arenas: 'general/arenas',
      autoScale: 'general/videoAutoScale',
      config: 'general/config',
      globalStreamUrl: 'general/streamUrl',
      globalStreamWatermarkData: 'general/streamWatermarkData',
      arenaStream: 'general/arenaStream',
      streamError: 'general/streamError',
      pipDisabled: 'general/pipDisabled',
    }),
    /*
     * The arena this player is for. Almost always the selected arena -- and in a
     * cockfight pane its scoped one. A player given an `arenaId` for a different
     * arena (the Tài Xỉu pane's table, whose arena is never the root selection)
     * takes that arena from the list instead, so it asks for the right stream and
     * reads the right arena's status rather than whatever is selected.
     */
    arena() {
      const selected = this.selectedArena;
      if (!this.arenaId || (selected && selected.id === this.arenaId)) return selected;
      return (this.arenas || []).find((item) => item.id === this.arenaId) || { id: this.arenaId };
    },
    streamArenaId() {
      return this.arenaId || (this.arena && this.arena.id) || '';
    },
    /*
     * Prefer this player's own arena stream when one exists, so the desktop
     * split view can run two players at once; fall back to the global for every
     * existing caller, leaving mobile and v1 desktop unchanged.
     */
    ownArenaStream() {
      return this.arenaStream(this.streamArenaId);
    },
    streamUrl() {
      /*
       * An explicit arenaId means this player owns one specific arena, so it
       * must never fall back to the shared "most recent response" global -- on
       * first load that is whichever arena replied last, which showed both
       * desktop panes the same feed. Empty until this arena's own reply lands.
       */
      if (this.arenaId) return (this.ownArenaStream && this.ownArenaStream.url) || '';
      // No arenaId (the /v1 page): prefer this arena's own reply, so a reply for
      // another arena landing in the shared global cannot switch the channel.
      // The global is only the fallback until this arena's reply arrives.
      return (this.ownArenaStream && this.ownArenaStream.url) || this.globalStreamUrl;
    },
    streamWatermarkData() {
      if (this.arenaId) return this.ownArenaStream || {};
      if (this.ownArenaStream && this.ownArenaStream.url) return this.ownArenaStream;
      return this.globalStreamWatermarkData;
    },
    isPipDisabled() {
      return AntiPipLayer.isPipDisabled(this.pipDisabled);
    },
    isReverse() {
      return this.arena.isReverse;
    },
    bankerOdds() {
      const banker = find(this.fight.selections, (s) => s.name === 'banker');
      return banker && !isEmpty(banker) ? `${banker.odds}x` : '';
    },
    playerOdds() {
      const player = find(this.fight.selections, (s) => s.name === 'player');
      return player && !isEmpty(player) ? `${player.odds}x` : '';
    },
    isMobileLandscape() {
      return isMobile && this.$screen.width > this.$screen.height;
    },
    // Start muted when no preference exists; restore an unmuted preference only after playback starts.
    effectiveInitialMuted() {
      return this.savedMuted !== null ? this.savedMuted : true;
    },
    isMatchDayEnding() {
      return this.arena.isMatchDayEnding;
    },
    // streamUrl() {
    //   if (typeof this.arena.streamUrl === 'string') {
    //     return this.arena.streamUrl;
    //   }

    //   if (typeof this.arena.streamUrl === 'object') {
    //     return this.arena.streamUrl[this.streamUrlSelector] || this.arena.streamUrl['default'] || '';
    //   }

    //   return '';
    // },
    // streamUrlSelector() {
    //   return this.config.language === 'en' ? 'default' : this.config.language;
    // },
    playerOptions() {
      const options = {
        width: '100%',
        aspectRatio: '16:9',
        muted: true,
        language: this.config.language | 'en',
        autoplay: true,
        controls: true,
        isLive: true,
        fluid: false,
        poster: '../../assets/images/video-poster.jpg',
        notSupportedMessage: "Your browser can't support our fommat, please try other browser",
        sources: [
          {
            src: this.streamUrl,
          },
        ],
        controlBar: {
          pictureInPictureToggle: !this.isPipDisabled, // Disable PiP when pip=false
          fullscreenToggle: !isIOS,
          remainingTimeDisplay: false,
          durationDisplay: false,
          timeDivider: false,
          currentTimeDisplay: false,
          autoScaleButton: !isIOS,
        },
      };
      if (this.streamUrl) {
        options.sources = [
          {
            src: this.streamUrl,
          },
        ];
      }
      return options;
    },
    watermarkMovementType() {
      return this.streamWatermarkData.watermarkMovement || 0;
    },
    watermarkStyle() {
      const type = this.watermarkMovementType;

      const fontSizeFromBE =
        this.streamWatermarkData.watermarkFontSize != null &&
        !Number.isNaN(Number(this.streamWatermarkData.watermarkFontSize)) &&
        Number(this.streamWatermarkData.watermarkFontSize) > 0
          ? Math.round(Number(this.streamWatermarkData.watermarkFontSize))
          : null;

      // Static or Random - use calculated position
      if (type === 0 || type === 1) {
        const style = {
          top: `${this.watermarkPosition.y}%`,
          left: `${this.watermarkPosition.x}%`,
        };
        if (fontSizeFromBE !== null) {
          style.fontSize = `${fontSizeFromBE}px`;
        }

        // Only add transition for static (type 0), not for random movement (type 1)
        if (type === 0) {
          style.transition = 'top 0.3s ease, left 0.3s ease';
        }

        return style;
      }

      // Corner-to-corner movement (Type 3) - Diagonal movement from edges
      if (type === 3) {
        const style = {
          transition: 'top 2s ease, left 2s ease, opacity 0.3s ease',
        };
        if (fontSizeFromBE !== null) {
          style.fontSize = `${fontSizeFromBE}px`;
        }

        // Path 1: from LEFT edge (20-30% from top) moving diagonally to TOP edge
        if (this.currentCorner === 'top-left') {
          if (this.cornerAnimationPhase === 'start') {
            // Start at left edge, 20-30% from top
            style.top = '25%';
            style.left = '0%';
            style.opacity = '0';
          } else {
            // End at top edge, 10-20% from left, then disappear
            style.top = '0%';
            style.left = '15%';
            style.opacity = '1';
          }
        }
        // Path 2: from BOTTOM edge (10-20% from right) moving diagonally to RIGHT edge
        else {
          if (this.cornerAnimationPhase === 'start') {
            // Start at bottom edge, 10-20% from right
            style.top = '100%';
            style.left = '75%';
            style.opacity = '0';
          } else {
            // End at right edge (middle height), then disappear
            style.top = '75%';
            style.left = '95%';
            style.opacity = '1';
          }
        }

        return style;
      }

      // Marquee - no position style (handled by CSS animation)
      return fontSizeFromBE !== null ? { fontSize: `${fontSizeFromBE}px` } : {};
    },
    robustWatermarkOverlayStyle() {
      return {
        top: `${this.robustWatermarkPosition.y}%`,
        left: `${this.robustWatermarkPosition.x}%`,
      };
    },
    watermarkType5Style() {
      // Get base positioning from watermarkStyle
      const baseStyle = this.watermarkStyle;

      // Get color and opacity from backend, with sensible defaults for subtle watermark
      // Default: white color with 30% opacity for minimal disruption
      const watermarkColor = this.streamWatermarkData.watermarkColor || '#FFFFFF';
      const watermarkOpacity = this.streamWatermarkData.watermarkOpacity || 0.4;

      return {
        ...baseStyle,
        color: watermarkColor,
        opacity: watermarkOpacity,
      };
    },
  },
  methods: {
    ...mapActions('general', ['updateGeneral', 'getArenas', 'getArenaStreamUrl']),
    ...mapActions('fight', ['resetFight', 'resetFights', 'resetFightsPage']),
    isEmpty,
    getImgUrl,
    getBaseUrl,

    getWatermarkImageUrl() {
      if (!this.streamWatermarkData || !this.streamWatermarkData.watermarkImageUrl) {
        return '';
      }

      const imageUrl = this.streamWatermarkData.watermarkImageUrl;
      const isProduction = isProdMode() || process.env.NODE_ENV === 'production';

      // If it's a relative path, prepend with base URL
      const baseUrl = getBaseUrl();
      const separator = imageUrl.startsWith('/') ? '' : '/';
      return `https://${isProduction ? 'api' : 'gc-api-dev'}.${baseUrl}${separator}${imageUrl}`;
    },

    clearAndroidAutoplayRetry() {
      if (this.androidAutoplayTimer) {
        clearTimeout(this.androidAutoplayTimer);
        this.androidAutoplayTimer = null;
      }
    },

    scheduleAndroidAutoplayRetry(reinitialize = false) {
      if (!isAndroid || this.androidAutoplayTimer || this.androidAutoplayRetryCount >= 3) return;
      this.androidAutoplayTimer = setTimeout(() => {
        this.androidAutoplayTimer = null;
        if (reinitialize && !this.tcPlayer) {
          this.startPlay();
          return;
        }
        this.ensureAndroidAutoPlay();
      }, 700);
    },

    ensureAndroidAutoPlay() {
      if (!isAndroid || !this.streamUrl) return;
      if (this.androidPlayPending) return;
      if (!this.tcPlayer) {
        this.androidAutoplayRetryCount++;
        this.scheduleAndroidAutoplayRetry(true);
        return;
      }

      try {
        const desiredMuted = this.androidDesiredMuted !== null ? this.androidDesiredMuted : this.savedMuted;
        const desiredVolume = this.androidDesiredVolume !== null ? this.androidDesiredVolume : this.savedVolume;
        // Chrome Android only permits programmatic autoplay while muted.
        this.androidPlayPending = true;
        this.tcPlayer.muted(true);
        const video = this.tcPlayer.el() && this.tcPlayer.el().querySelector('video');
        if (video) video.muted = true;

        const restoreAudio = () => {
          this.androidPlayPending = false;
          this.androidAutoplayRetryCount = 0;
          this.autoplaySucceeded = true;
          this.clearAndroidAutoplayRetry();
          if (desiredMuted === false && this.tcPlayer) {
            this.tcPlayer.muted(false);
            if (desiredVolume !== null) this.tcPlayer.volume(desiredVolume);
          }
        };

        if (!this.tcPlayer.paused()) {
          restoreAudio();
          return;
        }

        const playPromise = this.tcPlayer.play();
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise.then(restoreAudio).catch(() => {
            this.androidPlayPending = false;
            this.androidAutoplayRetryCount++;
            this.scheduleAndroidAutoplayRetry();
          });
        } else {
          this.androidPlayPending = false;
        }
      } catch (error) {
        this.androidPlayPending = false;
        this.androidAutoplayRetryCount++;
        this.scheduleAndroidAutoplayRetry();
      }
    },

    startPlay() {
      // Check if stream URL is available
      if (!this.streamUrl) return;

      // If player already exists, don't reinitialize (unless we're forcing recreation)
      if (this.tcPlayer || this.isPlayerInitializing) return;
      this.isPlayerInitializing = true;
      // if (!streamUrl) {
      //   console.warn('Stream URL not available yet, waiting...');
      //   setTimeout(() => this.startPlay(), 500);
      //   return;
      // }
      // Wait for DOM to be ready
      this.$nextTick(() => {
        // Multiple immediate watchers/mounted hooks can queue startPlay before TCPlayer exists.
        if (this.tcPlayer) {
          this.isPlayerInitializing = false;
          return;
        }

        // Make sure the player element exists
        if (!document.getElementById(this.playerDomId)) {
          // Try again after a delay
          this.isPlayerInitializing = false;
          setTimeout(() => this.startPlay(), 100);
          return;
        }

        // Read the latest URL after the async DOM wait; the watcher may have received a newer arena URL.
        const streamUrl = normalizeStreamUrl(this.streamUrl);
        if (!streamUrl) {
          this.isPlayerInitializing = false;
          return;
        }

        try {
          // Initialize with explicit type for the source
          this.tcPlayer = TCPlayer(this.playerDomId, {
            ...this.playerOptions,
            sources: [
              {
                src: streamUrl,
                type: 'application/x-mpegURL',
              },
            ],
          });
          this.isPlayerInitializing = false;

          // Restore persisted audio state so that if the user has unmuted
          // before switching away (e.g. to canvas) and comes back, the
          // volume remains in the same state. Mobile must remain muted until
          // autoplay succeeds; its state is restored after playback starts.
          if (!isMobile && this.savedMuted !== null) {
            this.tcPlayer.muted(this.savedMuted);
          }
          if (!isMobile && this.savedVolume !== null && this.savedMuted === false) {
            try {
              this.tcPlayer.volume(this.savedVolume);
            } catch (e) {
              // Ignore invalid volume values
            }
          }

          // Persist audio changes whenever the user adjusts mute/volume.
          this.tcPlayer.on('volumechange', () => {
            try {
              // Do not overwrite the user's audio preference with the temporary
              // mute required by Chrome Android's autoplay policy.
              if (isAndroid && this.androidPlayPending && !this.autoplaySucceeded) return;
              const muted = this.tcPlayer.muted();
              const volume = this.tcPlayer.volume();

              this.savedMuted = muted;
              this.savedVolume = volume;
              if (isAndroid && this.autoplaySucceeded) {
                this.androidDesiredMuted = muted;
                this.androidDesiredVolume = volume;
              }

              window.localStorage.setItem('tcPlayerMuted', String(muted));
              window.localStorage.setItem('tcPlayerVolume', String(volume));
            } catch (e) {
              // Ignore storage issues (e.g. in private mode)
            }
          });

          // Add error handling — TCPlayer/Video.js passes event; get code from player.error()
          this.tcPlayer.on('error', () => {
            const err = this.tcPlayer && this.tcPlayer.error ? this.tcPlayer.error() : null;
            const code = err != null ? Number(err.code) : undefined;
            if (code === 4 || code === 3) {
              const now = Date.now();
              if (now - this.lastErrorRecoveryTime < 5000) return; // Debounce: 5s between attempts
              this.lastErrorRecoveryTime = now;

              this.stopRetry();
              this.requestNewStreamAndReinit();
            } else if (code === 14) {
              this.scheduleRetry();
            } else {
              this.scheduleRetry();
            }
          });

          // Add TCPlayer pause/play listeners (important for mobile)
          this.tcPlayer.on('play', () => {
            this.onPlayerPlay();
          });

          this.tcPlayer.on('pause', () => {
            this.onPlayerPause();
          });

          // Emit volume changes to parent; persist to localStorage on iOS (debounced)
          this.tcPlayer.on('volumechange', () => {
            if (!this.tcPlayer) return;
            const muted = this.tcPlayer.muted();
            this.$emit('volume-change', { muted });
            if (isIOS) this.debouncedPersistVolume(muted);
          });

          // Force autoplay on iOS (must be muted for programmatic play without user gesture)
          if (isIOS) {
            const tryPlay = () => {
              if (!this.tcPlayer || this.autoplaySucceeded) return;
              this.tcPlayer.muted(true);
              const v = this.tcPlayer.el().querySelector('video');
              if (v) v.muted = true;
              const p = this.tcPlayer.play();
              if (p && p.then) {
                p.then(() => {
                  this.autoplaySucceeded = true;
                }).catch(() => {});
              }
            };
            this.tcPlayer.one('canplay', tryPlay);
            this.scheduleIosAutoplayFallback();
          }

          if (isAndroid) {
            this.tcPlayer.one('loadeddata', () => this.ensureAndroidAutoPlay());
            this.tcPlayer.one('canplay', () => this.ensureAndroidAutoPlay());
            this.scheduleAndroidAutoplayRetry();
          }

          // Restore muted state from parent (or persisted) after playback starts
          if (!this.effectiveInitialMuted) {
            this.tcPlayer.one('playing', () => {
              if (this.tcPlayer) {
                this.tcPlayer.muted(false);
                this.tcPlayer.volume(1);
              }
            });
          }

          this.initCustomControlBar();

          // Initialize play time tracking when player is created
          this.lastPlayTime = Date.now();

          // Protect the video element from PiP if disabled
          if (this.antiPipLayer) {
            const videoElement = document.getElementById(this.playerDomId);
            if (videoElement) {
              this.antiPipLayer.protectVideoElement(videoElement);
            }
          }
        } catch (err) {
          this.isPlayerInitializing = false;
          console.error('[tc-video-v1] TCPlayer initialization failed:', err);
          if (isAndroid) {
            this.scheduleAndroidAutoplayRetry(true);
          }
        }
      });
    },
    // listen event
    getActiveResolution() {
      return this.resolutions[this.activeResolutionIndex];
    },
    getAutoScaleIcon() {
      return this.autoScale ? 'vjs-custom-update-button' : 'vjs-custom-update-disabled-button';
    },
    onPlayerPlay(player) {
      const now = Date.now();

      // Prevent duplicate processing (events can fire from both native and TCPlayer)
      if (this.lastPlayTime && now - this.lastPlayTime < 100) {
        return;
      }

      // Skip check if we just reinitialized
      if (this.skipNextPlayCheck) {
        this.skipNextPlayCheck = false;
        this.lastPlayTime = now;
        this.pauseStartTime = null;
        this.windowBlurTime = null;
        return;
      }

      // Check if we're resuming from a pause
      if (this.pauseStartTime) {
        const pauseDuration = now - this.pauseStartTime;

        // If paused for more than threshold, reinitialize to get latest segment
        if (pauseDuration > this.refreshThreshold) {
          this.reinitializePlayer();
          return;
        }
      }

      // Record when playback starts/resumes
      this.lastPlayTime = now;
      this.pauseStartTime = null;
      this.windowBlurTime = null;
    },
    onPlayerPause(player) {
      const now = Date.now();

      // Prevent duplicate processing
      if (this.pauseStartTime && now - this.pauseStartTime < 100) {
        return;
      }

      // Record when pause starts
      this.pauseStartTime = now;
    },
    onPlayerEnded(player) {
      // console.log('player ended!', player);
    },
    onPlayerLoadeddata(player) {
      // console.log('player Loadeddata!', player);
      // Note: stopRetry is handled in scheduleRetry's one-time listeners
    },
    onPlayerWaiting(player) {
      // console.log('player Waiting!', player);
    },
    onPlayerPlaying(player) {
      // Track active playback time
      this.lastPlayTime = Date.now();
      this.windowBlurTime = null;
      // Note: stopRetry is handled in scheduleRetry's one-time listeners
    },
    onPlayerTimeupdate(player) {
      // Update last play time on each time update
      this.lastPlayTime = Date.now();
    },
    onPlayerCanplay(player) {
      // console.log('player Canplay!', player);
    },
    onPlayerCanplaythrough(player) {
      // console.log('player Canplaythrough!', player);
    },

    // or listen state event
    playerStateChanged(playerCurrentState) {
      // console.log('player current update state', playerCurrentState);
    },
    scheduleIosAutoplayFallback() {
      if (this.iosAutoplayTimer) clearTimeout(this.iosAutoplayTimer);
      this.iosAutoplayTimer = setTimeout(() => {
        this.iosAutoplayTimer = null;
        this.ensureAutoPlay();
      }, 3000);
    },

    debouncedPersistVolume(muted) {
      if (this.volumePersistTimer) clearTimeout(this.volumePersistTimer);
      this.volumePersistTimer = setTimeout(() => {
        this.volumePersistTimer = null;
        if (!this.tcPlayer) return;
        try {
          window.localStorage.setItem('tcPlayerMuted', String(muted));
          const vol = this.tcPlayer.volume();
          if (typeof vol === 'number') {
            window.localStorage.setItem('tcPlayerVolume', String(vol));
          }
        } catch (e) {
          /* private mode */
        }
      }, 250);
    },

    initCustomControlBar() {
      if (!this.tcPlayer) return;
      // this.arenaBtns = [];
      const autoScaleBtn = this.tcPlayer.controlBar.addChild('button');

      // this.arenas.forEach((arena, index) => {
      //   const arenaBtn = this.tcPlayer.controlBar.addChild('button');
      //   arenaBtn.addClass('vjs-custom-arena-button');
      //   arenaBtn.addClass(`arena-${index + 1}`);

      //   arenaBtn.controlText(arena.name.default);
      //   arenaBtn.setAttribute('title', this.$t('{arena} Video Stream', { arena: arena.name.default }));

      //   if (arena.id === this.arena.id) {
      //     arenaBtn.addClass('active');
      //   }

      //   const clickHandler = () => {
      //     if (this.arena.id === arena.id) return;
      //     this.updateGeneral({ selectedArena: { ...arena } });
      //     this.resetFights();
      //     this.resetFight();
      //     this.resetFightsPage();
      //     this.tcPlayer.src({
      //       src: arena.streamUrl[this.streamUrlSelector],
      //     });
      //   };
      //   if (isMobile) {
      //     arenaBtn.on('tap', clickHandler);
      //   } else {
      //     arenaBtn.on('click', clickHandler);
      //   }
      //   this.arenaBtns.push(arenaBtn);
      // });

      autoScaleBtn.addClass('vjs-custom-update-button');
      if (!this.autoScale) {
        autoScaleBtn.addClass('disabled');
      }
      autoScaleBtn.setAttribute('title', this.$t('Toggle video auto scale'));

      const autoScaleStatusHandler = (e) => {
        e.preventDefault();
        if (!autoScaleBtn) return;
        if (this.autoScale) {
          autoScaleBtn.addClass('disabled');
        } else {
          autoScaleBtn.removeClass('disabled');
        }
        this.updateGeneral({ videoAutoScale: !this.autoScale });
      };

      autoScaleBtn.on('click', autoScaleStatusHandler);
      autoScaleBtn.on('touchstart', autoScaleStatusHandler);

      // Refresh stream button (reload icon)
      // const refreshBtn = this.tcPlayer.controlBar.addChild('button');
      // refreshBtn.addClass('vjs-custom-refresh-button');
      // refreshBtn.setAttribute('title', this.$t('Refresh stream'));

      const refreshStreamHandler = (e) => {
        e.preventDefault();
        this.refreshStreamUrl();
      };

      if (isMobile) {
        refreshBtn.on('touchstart', refreshStreamHandler);
      } else {
        refreshBtn.on('click', refreshStreamHandler);
      }
    },

    // Watermark control methods
    getExponentialJitter(meanSeconds) {
      const random = Math.random();
      return -meanSeconds * Math.log(1 - random);
    },

    startWatermarkFrequency() {
      if (!this.streamWatermarkData || typeof this.streamWatermarkData.watermarkFrequency === 'undefined') {
        this.watermarkVisible = false;
        this.stopContinuousMovement();
        return;
      }

      const frequency = this.streamWatermarkData.watermarkFrequency || 0;
      const config = this.frequencyConfig[frequency];

      if (!config) {
        this.watermarkVisible = false;
        this.stopContinuousMovement();
        return;
      }

      if (this.watermarkTimer) {
        clearTimeout(this.watermarkTimer);
        this.watermarkTimer = null;
      }

      if (config.mode === 'off') {
        this.watermarkVisible = false;
        this.stopContinuousMovement();
        return;
      }

      if (config.mode === 'always_on') {
        this.watermarkVisible = true;
        this.randomizeWatermarkPosition();
        this.startContinuousMovement();
        return;
      }

      // Start visible
      this.watermarkVisible = true;
      this.randomizeWatermarkPosition();
      this.startContinuousMovement();
      this.scheduleWatermarkToggle();
    },

    scheduleWatermarkToggle() {
      const frequency = this.streamWatermarkData.watermarkFrequency || 0;
      const config = this.frequencyConfig[frequency];

      if (!config || config.mode === 'off' || config.mode === 'always_on') {
        return;
      }

      let delay;
      if (this.watermarkVisible) {
        delay = this.getExponentialJitter(config.mean_on_s) * 1000;
      } else {
        delay = this.getExponentialJitter(config.mean_off_s) * 1000;
      }

      this.watermarkTimer = setTimeout(() => {
        const wasVisible = this.watermarkVisible;
        this.watermarkVisible = !this.watermarkVisible;

        if (this.watermarkVisible && !wasVisible) {
          this.randomizeWatermarkPosition();
        }

        this.scheduleWatermarkToggle();
      }, delay);
    },

    stopWatermarkFrequency() {
      if (this.watermarkTimer) {
        clearTimeout(this.watermarkTimer);
        this.watermarkTimer = null;
      }
    },

    randomizeWatermarkPosition() {
      const type = this.watermarkMovementType;

      // Marquee and corner-to-corner don't need position randomization
      if (type === 2 || type === 3) {
        return;
      }

      // Random position within 0-70% of container (to keep watermark visible)
      this.watermarkPosition.x = Math.random() * 70;
      this.watermarkPosition.y = Math.random() * 70;

      // For type 1 (random movement), also set target for continuous animation
      if (type === 1) {
        this.watermarkPosition.targetX = Math.random() * 70;
        this.watermarkPosition.targetY = Math.random() * 70;
      }
    },

    randomizeRobustWatermarkPosition() {
      // Random position within 5-75% to keep watermark visible in video area
      this.robustWatermarkPosition.x = 5 + Math.random() * 70;
      this.robustWatermarkPosition.y = 5 + Math.random() * 70;
      this.robustWatermarkPosition.targetX = 5 + Math.random() * 70;
      this.robustWatermarkPosition.targetY = 5 + Math.random() * 70;
    },

    startRobustWatermarkMovement() {
      this.stopRobustWatermarkMovement();
      this.randomizeRobustWatermarkPosition();

      const config = ROBUST_WATERMARK_MODERATE;
      const speed = config.interpolationSpeed;
      const threshold = config.targetDistanceThreshold;

      this.robustWatermarkMoveTimer = setInterval(() => {
        const pos = this.robustWatermarkPosition;
        if (pos.targetX === undefined || pos.targetY === undefined) {
          pos.targetX = 5 + Math.random() * 70;
          pos.targetY = 5 + Math.random() * 70;
        }

        const dx = pos.targetX - pos.x;
        const dy = pos.targetY - pos.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < threshold) {
          pos.targetX = 5 + Math.random() * 70;
          pos.targetY = 5 + Math.random() * 70;
        } else {
          pos.x += dx * speed;
          pos.y += dy * speed;
        }
      }, 50);
    },

    stopRobustWatermarkMovement() {
      if (this.robustWatermarkMoveTimer) {
        clearInterval(this.robustWatermarkMoveTimer);
        this.robustWatermarkMoveTimer = null;
      }
    },

    // Start continuous movement animation (for type 1 - random movement, type 3 - corner movement)
    startContinuousMovement() {
      // Clear any existing movement timer
      this.stopContinuousMovement();

      // For movement type 1 (random)
      if (this.watermarkMovementType === 1) {
        // Update position every 50ms (~20fps) for smooth movement
        this.watermarkMovementTimer = setInterval(() => {
          if (!this.watermarkVisible) return;

          // Initialize target if not set
          if (this.watermarkPosition.targetX === undefined) {
            this.watermarkPosition.targetX = Math.random() * 70;
            this.watermarkPosition.targetY = Math.random() * 70;
          }

          // Calculate distance to target
          const dx = this.watermarkPosition.targetX - this.watermarkPosition.x;
          const dy = this.watermarkPosition.targetY - this.watermarkPosition.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          // If close to target, pick new target
          if (distance < 1) {
            this.watermarkPosition.targetX = Math.random() * 70;
            this.watermarkPosition.targetY = Math.random() * 70;
          } else {
            // Smooth interpolation towards target
            const speed = 0.05; // Adjust for speed (0.01 = slow, 0.1 = fast)
            this.watermarkPosition.x += dx * speed;
            this.watermarkPosition.y += dy * speed;
          }
        }, 50);
      }

      // For movement type 3 (diagonal movement alternating between corners)
      if (this.watermarkMovementType === 3) {
        // Initialize at top-left corner, start position
        this.currentCorner = 'top-left';
        this.cornerAnimationPhase = 'start';

        // Animate within current corner, then switch corners
        this.watermarkMovementTimer = setInterval(() => {
          if (!this.watermarkVisible) return;

          if (this.cornerAnimationPhase === 'start') {
            // Move to end position (diagonal movement)
            this.cornerAnimationPhase = 'end';
          } else {
            // Switch to other corner and reset to start
            this.currentCorner = this.currentCorner === 'top-left' ? 'bottom-right' : 'top-left';
            this.cornerAnimationPhase = 'start';
          }
        }, 2000); // 2 seconds per step
      }
    },

    stopContinuousMovement() {
      if (this.watermarkMovementTimer) {
        clearInterval(this.watermarkMovementTimer);
        this.watermarkMovementTimer = null;
      }
    },

    // Window focus/blur handlers (for debugging only, no reinit)
    onWindowFocus() {
      this.windowFocusTime = Date.now();
    },

    onWindowBlur() {
      this.windowBlurTime = Date.now();
    },

    // Handle browser tab/app visibility change (same logic as tc-video.vue handleVisibilityChange)
    onVisibilityChange() {
      if (document.hidden) {
        this.pageHiddenTime = Date.now();
        this.windowBlurTime = Date.now();
      } else {
        this.windowFocusTime = Date.now();
        if (this.pageHiddenTime) {
          const hiddenDuration = Date.now() - this.pageHiddenTime;

          // If hidden for longer than 20 seconds, request fresh stream URL and reinit when it arrives
          if (hiddenDuration > 20000 && this.arena && this.arena.id) {
            this.stopRetry();
            this.requestNewStreamAndReinit();
          }
          // If hidden for shorter time but longer than threshold, refresh to get latest stream (Chrome/Safari tab return)
          else if (hiddenDuration > this.visibilityThreshold && this.streamUrl && !this.isReinitializing) {
            this.refreshStreamUrl();
          }

          this.pageHiddenTime = null;
        }

        this.ensureAutoPlay();
      }
    },

    // Fetch latest stream from backend then reinit via tcPlayer.src() when new URL arrives
    refreshStreamOnVisibilityReturn() {
      if (!this.arena.id) return;
      this.requestNewStreamAndReinit();
    },

    // Dispose player and recreate from scratch (used by pull-to-refresh when src() is not enough)
    fullRecreatePlayer() {
      if (!this.streamUrl) return;

      this.isReinitializing = true;
      this.skipNextPlayCheck = true;
      this.pauseStartTime = null;
      this.ensureAutoPlayRetryCount = 0;
      this.androidAutoplayRetryCount = 0;
      this.androidPlayPending = false;
      this.autoplaySucceeded = false;
      this.isPlayerInitializing = false;
      this.clearAndroidAutoplayRetry();
      this.stopRetry();

      if (this.tcPlayer) {
        try {
          this.tcPlayer.pause();
          this.tcPlayer.dispose();
        } catch (e) {
          // ignore
        }
        this.tcPlayer = null;
      }

      // Force Vue to destroy and re-create the video element so we get a fresh decoder state
      this.videoPlayerKey += 1;

      this.$nextTick(() => {
        this.$nextTick(() => {
          this.isReinitializing = false;
          this.startPlay();
          // iOS: HLS needs more time after recreate; longer delay + retry on failure
          const delayMs = isIOS ? 2500 : 800;
          setTimeout(() => this.ensureAutoPlay(true), delayMs);
        });
      });
    },

    // Ensure video is playing after visibility change / pull-to-refresh on mobile
    ensureAutoPlay(retryFromRecreate = false) {
      if (isAndroid) {
        this.ensureAndroidAutoPlay();
        return;
      }

      if (this.tcPlayer && this.streamUrl) {
        try {
          if (this.tcPlayer.paused()) {
            // iOS requires muted for programmatic play without user gesture
            if (isIOS) {
              this.tcPlayer.muted(true);
              const videoEl = this.tcPlayer.el().querySelector('video');
              if (videoEl) videoEl.muted = true;
            }
            const playPromise = this.tcPlayer.play();
            if (playPromise && typeof playPromise.then === 'function') {
              playPromise
                .then(() => {
                  this.ensureAutoPlayRetryCount = 0;
                  this.autoplaySucceeded = true;
                  // Unmute handled by existing 'playing' listener in startPlay
                })
                .catch(() => {
                  // Retry after fullRecreate (HLS may need more time to load)
                  if (retryFromRecreate && isIOS && this.ensureAutoPlayRetryCount < 3) {
                    this.ensureAutoPlayRetryCount++;
                    setTimeout(() => this.ensureAutoPlay(true), 1000);
                  }
                });
            }
          }
        } catch (err) {
          if (retryFromRecreate && isIOS && this.ensureAutoPlayRetryCount < 3) {
            this.ensureAutoPlayRetryCount++;
            setTimeout(() => this.ensureAutoPlay(true), 1000);
          }
        }
      }
    },

    // Pull-to-refresh touch handlers (mobile)
    onPullTouchStart(event) {
      if (!isMobile || !this.streamUrl || this.isRefreshingStream) return;
      const touch = event.touches[0];
      this.pullStartY = touch.clientY;
      this.pullDistance = 0;
    },

    onPullTouchMove(event) {
      if (!isMobile || !this.streamUrl || this.isRefreshingStream || !this.pullStartY) return;

      const touch = event.touches[0];
      const deltaY = touch.clientY - this.pullStartY;

      // Only activate pull-to-refresh when pulling down with a minimum threshold
      if (deltaY > 10) {
        this.isPullingToRefresh = true;
        this.pullDistance = Math.min(deltaY * 0.5, this.pullRefreshThreshold * 1.5);
        // Prevent native iOS pull-to-refresh / scroll
        event.preventDefault();
      }
    },

    onPullTouchEnd() {
      if (!this.isPullingToRefresh) {
        this.resetPullState();
        return;
      }

      if (this.pullDistance >= this.pullRefreshThreshold) {
        this.refreshStreamUrl();
      }

      this.resetPullState();
    },

    resetPullState() {
      this.pullStartY = 0;
      this.pullDistance = 0;
      this.isPullingToRefresh = false;
    },

    // Request new stream URL and reinit; forces WebSocket reconnect and retries until new URL arrives
    requestNewStreamAndReinit() {
      if (!this.arena.id) return;
      this.clearError4Retry();
      this.pendingStreamReinit = true;
      this.error4RetryCount = 0;

      // Force WebSocket reconnect (fixes expired token after long background)
      const token = this.token || this.$store.getters['general/token'];
      if (token) {
        reconnectMainConnection(token);
      }

      const doRequest = () => {
        this.getArenaStreamUrl(this.arena.id);
      };
      doRequest();

      // Retry getArenaStreamUrl every 4s (WS may need time to reconnect); max 5 attempts
      const retryInterval = 4000;
      const maxRetries = 5;
      this.error4RetryTimer = setInterval(() => {
        if (!this.pendingStreamReinit) return; // Got new URL, stop retrying
        this.error4RetryCount += 1;
        if (this.error4RetryCount >= maxRetries) {
          this.clearError4Retry();
          if (this.tcPlayer && this.streamUrl) {
            this.pendingStreamReinit = false;
            this.fullRecreatePlayer();
          }
          return;
        }
        doRequest();
      }, retryInterval);
    },

    clearError4Retry() {
      if (this.error4RetryTimer) {
        clearInterval(this.error4RetryTimer);
        this.error4RetryTimer = null;
      }
    },

    // Refresh stream for pull-to-refresh: request new URL, reinit via src() when it arrives
    refreshStreamUrl() {
      if (this.isRefreshingStream || !this.arena.id) return;
      this.isRefreshingStream = true;
      this.requestNewStreamAndReinit();
      setTimeout(() => {
        this.isRefreshingStream = false;
      }, 3000);
    },

    // Mobile-specific handlers (iOS Safari, Chrome Android)
    onPageShow(event) {
      this.windowFocusTime = Date.now();
      // On mobile, get latest stream URL and autoplay after page restore / native pull-to-refresh
      if (isMobile && this.arena.id && this.streamUrl) {
        this.refreshStreamUrl();
      } else {
        this.ensureAutoPlay();
      }
    },

    onPageHide(event) {
      this.windowBlurTime = Date.now();
    },

    seekToLiveEdge() {
      if (!this.tcPlayer) {
        return;
      }

      try {
        // For HLS live streams, disposal and reinitialization is more reliable
        // than seeking, especially after long pauses
        this.reinitializePlayer();
      } catch (error) {
        this.reinitializePlayer();
      }
    },

    reinitializePlayer() {
      if (!this.streamUrl) {
        return;
      }

      if (this.isReinitializing) {
        return;
      }

      this.isReinitializing = true;
      this.skipNextPlayCheck = true;
      this.pauseStartTime = null;

      // Normalize URL for iOS devices
      const normalizedUrl = normalizeStreamUrl(this.streamUrl);

      // Add cache-busting parameter to force fresh HLS manifest fetch
      const timestamp = Date.now();
      const urlWithCache = normalizedUrl.includes('?')
        ? `${normalizedUrl}&_=${timestamp}`
        : `${normalizedUrl}?_=${timestamp}`;

      if (!this.tcPlayer) {
        this.isReinitializing = false;
        return;
      }

      // Load new source
      this.tcPlayer.src({
        src: urlWithCache,
        type: 'application/x-mpegURL',
      });

      // When new source is loaded, it will start from live edge
      this.tcPlayer.one('loadedmetadata', () => {
        if (!this.tcPlayer) return; // Component may have been destroyed
        const playPromise = this.tcPlayer.play();

        // Handle mobile autoplay restrictions
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              // Playback started successfully
            })
            .catch((error) => {
              // On mobile, user interaction may be required
              this.isReinitializing = false;
            });
        }
      });

      // Reset flag when playback starts
      this.tcPlayer.one('playing', () => {
        setTimeout(() => {
          this.isReinitializing = false;
        }, 500);
      });

      // Fallback timeout in case events don't fire (common on mobile)
      setTimeout(() => {
        if (this.isReinitializing) {
          this.isReinitializing = false;
        }
      }, 5000);
    },

    refreshStream() {
      // Alias to reinitializePlayer for consistency
      this.reinitializePlayer();
    },

    scheduleRetry() {
      if (this.retryTimer) return;

      this.retryCount++;

      if (this.retryCount > this.maxRetries) {
        this.retryCount = 0;
        return;
      }

      // Use simple setTimeout - most performant timer
      this.retryTimer = setTimeout(() => {
        this.retryTimer = null; // Clear reference immediately

        if (!this.tcPlayer || !this.streamUrl) {
          this.scheduleRetry(); // Try again
          return;
        }

        // Simple source update - no player recreation
        this.tcPlayer.src({
          src: normalizeStreamUrl(this.streamUrl),
          type: 'application/x-mpegURL',
        });

        // Track if success was handled to prevent duplicates
        let successHandled = false;
        let errorCheckTimeout = null;

        // Listen for success (one-time listener to prevent memory leaks)
        const onSuccess = () => {
          if (successHandled) return; // Already handled
          successHandled = true;

          // Clear the error check timeout
          if (errorCheckTimeout) {
            clearTimeout(errorCheckTimeout);
            errorCheckTimeout = null;
          }
          this.stopRetry(); // Stop retry mechanism completely

          // Remove the other listener to prevent duplicate call
          this.tcPlayer.off('loadedmetadata', onSuccess);
          this.tcPlayer.off('playing', onSuccess);
        };

        // Listen for successful connection
        this.tcPlayer.one('loadedmetadata', onSuccess);
        this.tcPlayer.one('playing', onSuccess);

        // If still error after some time, continue retry
        errorCheckTimeout = setTimeout(() => {
          // If success handler wasn't called, assume failure and continue retry
          if (!successHandled && this.retryCount > 0) {
            this.scheduleRetry();
          }
        }, 4000);
      }, 5000);
    },

    stopRetry() {
      // Only run if retry is actually active
      if (!this.retryTimer && this.retryCount === 0) {
        return; // Already stopped
      }

      // Clear retry timer
      if (this.retryTimer) {
        clearTimeout(this.retryTimer);
        this.retryTimer = null;
      }
      // Reset retry count
      this.retryCount = 0;
    },

    convertStreamErrorMessage(errorMessage) {
      const match = errorMessage.match(/([A-Z]{3})\s*(\d+)/);
      if (match) {
        const currentCurrency = match[1];
        const currentBalance = match[2];
        return this.$t('You need at least {CURRENCY} {AMOUNT} in your wallet to watch this stream.', {
          CURRENCY: currentCurrency,
          AMOUNT: currentBalance,
        });
      }
      return errorMessage;
    },
  },
  watch: {
    'fight.status': {
      handler(newVal, oldVal) {
        if (this.isAnyPopupOpen) return;
        if (FightStatus[newVal] === FightStatus.started) {
          if (!isMobile && this.$refs.videoPlayerContainer) {
            this.screenScale = 1 + 285 / (this.$refs.videoPlayerContainer.clientHeight + 50);
          } else if (this.isMobileLandscape && this.$refs.videoPlayerContainer) {
            this.screenScale = this.$screen.height / this.$refs.videoPlayerContainer.clientHeight;
          } else {
            this.screenScale = 1;
          }
        } else {
          this.screenScale = 1;
        }
      },
      immediate: true,
    },
    streamUrl: {
      handler(newVal, oldVal) {
        if (!newVal) return;
        if (this.tcPlayer) {
          if (this.pendingStreamReinit) {
            this.pendingStreamReinit = false;
            this.clearError4Retry();
            this.fullRecreatePlayer();
          } else if (oldVal !== undefined) {
            if (isAndroid) {
              if (this.autoplaySucceeded) {
                this.androidDesiredMuted = this.tcPlayer.muted();
                this.androidDesiredVolume = this.tcPlayer.volume();
              }
              this.autoplaySucceeded = false;
              this.androidAutoplayRetryCount = 0;
              this.androidPlayPending = false;
            }
            this.tcPlayer.src({
              src: normalizeStreamUrl(newVal),
              type: 'application/x-mpegURL',
            });
            if (isAndroid) {
              this.tcPlayer.one('loadedmetadata', () => this.ensureAndroidAutoPlay());
              this.tcPlayer.one('canplay', () => this.ensureAndroidAutoPlay());
              this.scheduleAndroidAutoplayRetry();
            }
          }
        } else {
          this.startPlay();
        }
      },
      immediate: true,
    },
    'config.language': {
      handler(newVal, oldVal) {
        if (!newVal || !oldVal) return;
        this.getArenaStreamUrl(this.arena.id);
      },
      immediate: true,
    },
    arena: {
      handler(newVal, oldVal) {
        if (!newVal || !oldVal || newVal.id === oldVal.id) return;
        this.getArenaStreamUrl(newVal.id);
        // if (this.tcPlayer) {
        //   this.tcPlayer.src({
        //     src: this.streamUrl,
        //     type: 'application/x-mpegURL',
        //   });
        // }
        // if (!this.arenaBtns || isEmpty(this.arenaBtns)) return;
        // this.arenaBtns.forEach((b) => {
        //   if (b) {
        //     b.removeClass('active');
        //   }
        // });
        // const activeArenaIndex = findIndex(this.arenas, (a) => a.id === this.arena.id);
        // if (activeArenaIndex >= 0 && this.arenaBtns[activeArenaIndex]) {
        //   this.arenaBtns[activeArenaIndex].addClass('active');
        // }
      },
      immediate: true,
    },
    // arenas: {
    //   handler(val) {
    //     if (this.arenaBtns.length > 0) return;
    //     if (!val || isEmpty(val)) return;
    //     this.initCustomControlBar();
    //   },
    //   immediate: true,
    // },
    // 'config.language': {
    //   handler(newVal, oldVal) {
    //     if (oldVal !== newVal && this.tcPlayer && this.streamUrl) {
    //       this.tcPlayer.src({
    //         src: this.streamUrl,
    //         type: 'application/x-mpegURL',
    //       });
    //     }
    //   },
    // },
    streamWatermarkData: {
      handler(newVal, oldVal) {
        if (!newVal) return;

        this.stopWatermarkFrequency();
        this.startWatermarkFrequency();

        const watermarkType = Number((newVal && newVal.watermarkType) || 1);
        if (watermarkType === 3 || watermarkType === 4) {
          this.startRobustWatermarkMovement();
        } else {
          this.stopRobustWatermarkMovement();
        }
      },
      deep: true,
      immediate: true,
    },
  },
  beforeDestroy() {
    // Stop retry mechanism immediately
    this.stopRetry();
    this.isPlayerInitializing = false;
    this.androidPlayPending = false;
    this.clearAndroidAutoplayRetry();

    if (this.volumePersistTimer) {
      clearTimeout(this.volumePersistTimer);
      this.volumePersistTimer = null;
    }
    if (this.iosAutoplayTimer) {
      clearTimeout(this.iosAutoplayTimer);
      this.iosAutoplayTimer = null;
    }
    this.clearError4Retry();

    if (this.antiPipLayer) {
      this.antiPipLayer.destroy();
      this.antiPipLayer = null;
    }

    this.stopWatermarkFrequency();
    this.stopContinuousMovement();
    this.stopRobustWatermarkMovement();

    // Remove window focus/blur listeners
    window.removeEventListener('focus', this.onWindowFocus);
    window.removeEventListener('blur', this.onWindowBlur);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);

    // Remove mobile-specific listeners
    if (isMobile) {
      window.removeEventListener('pageshow', this.onPageShow);
      window.removeEventListener('pagehide', this.onPageHide);
    }

    // Remove pull-to-refresh listeners (mobile)
    if (isMobile) {
      const container = this.$refs.videoPlayerContainer;
      if (container) {
        container.removeEventListener('touchstart', this.onPullTouchStart);
        container.removeEventListener('touchmove', this.onPullTouchMove);
        container.removeEventListener('touchend', this.onPullTouchEnd);
      }
    }

    if (this.tcPlayer) {
      try {
        this.tcPlayer.pause();
        this.tcPlayer.dispose();
      } catch (e) {
        console.warn('Error disposing tcPlayer:', e);
      }
      this.tcPlayer = null;
    }

    const remainingVideos = document.querySelectorAll('#videoPlayerId, #videoPlayerId_html5_api');
    remainingVideos.forEach((el) => {
      try {
        if (el.tagName === 'VIDEO') {
          el.muted = true; // Mute first to prevent audio bleed
          el.pause();
          el.removeAttribute('src');
          el.load(); // Forces Safari to release the hardware decoder
        }
        el.remove();
      } catch (e) {
        // Ignore cleanup errors
      }
    });
  },
};
</script>

<style lang="scss">
.tc-video-player-box {
  min-height: 200px;
  position: relative;
  z-index: 1002;
  transform-origin: left top;
  transition: all 1s ease;

  &:before {
    content: '';
    background-image: url('~@/assets/images/shine.png');
    height: 30px;
    width: 100%;
    z-index: 2;
    bottom: 0;
    left: 50%;
    display: block;
    position: absolute;
    transform: translateX(-50%);
    background-size: cover;
    pointer-events: none;
  }

  .no-active-arena {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background-color: #0d1a24;
    aspect-ratio: 16/9;

    &.match-day-ending{
      background: url('~@/assets/images/ended.webp') no-repeat center center;
      background-size: contain;
    }

    @supports not (aspect-ratio: 16/9) {
      &::before {
        float: left;
        padding-top: 56.25%;
        content: '';
      }

      &::after {
        display: block;
        content: '';
        clear: both;
      }
    }

    img {
      height: 6.4rem;
      width: 6.4rem;
    }

    .text {
      text-align: center;
      font-size: 16px;
      color: #b2d6ea;

      &.my-3 {
        margin-top: 1rem;
        margin-bottom: 1rem;
      }
    }
  }

  .video-player-key-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .video-wrapper {
    position: relative;
    width: 100%;
    aspect-ratio: 16/9;
    background-color: #0d1a24;

    @supports not (aspect-ratio: 16/9) {
      &::before {
        float: left;
        padding-top: 56.25%; /* 16:9 aspect ratio */
        content: '';
      }

      &::after {
        display: block;
        content: '';
        clear: both;
      }
    }
  }

  /*排序顺序*/
  /* Volume + play on the right: apply to control bars anywhere under player (in case TCPlayer structure varies) */
  .vjs-control-bar2 .vjs-volume-panel,
  .vjs-control-bar2 .vjs-volume-menu-button,
  .vjs-control-bar .vjs-volume-panel,
  .vjs-control-bar .vjs-volume-menu-button {
    order: 1 !important;
    margin-left: auto !important;
  }

  .video-js {
    font-family: 'PingFang SC', 'Helvetica Neue', 'Hiragino Sans GB', 'Segoe UI', 'Microsoft YaHei', '微软雅黑',
      sans-serif;
    width: 100%;

    .vjs-custom-update-button {
      display: none !important;
    }

    .vjs-menu-button-inline.vjs-slider-active {
      width: 100px;
    }

    .vjs-menu-button-inline {
      &:focus {
        width: 100px;
      }

      &:hover {
        width: 100px;
      }

      &:before {
        width: 15px;
      }
    }

    .vjs-big-play-button {
      display: none !important;
    }

    // .vjs-control {
    //   width: 50px;
    // }

    .vjs-control.vjs-live-control {
      width: auto;
      padding-left: 5px;
      letter-spacing: 1px;
    }

    .vjs-load-progress {
      div {
        display: none !important;
      }
    }

    .vjs-mouse-display {
      &:after {
        padding: 0 0.4em 3px;
        border-radius: 0;
        font-size: 10px;
        padding: 0;
        width: 30px;
        height: 15px;
        line-height: 15px;
        top: -30px;
      }
    }

    .vjs-play-progress {
      &:after {
        padding: 0 0.4em 3px;
        border-radius: 0;
        font-size: 10px;
        padding: 0;
        width: 30px;
        height: 15px;
        line-height: 15px;
        top: -30px;
      }

      background-color: #2483d5;

      &:before {
        top: -3px;
      }
    }

    .vjs-big-play-button {
      top: 50% !important;
      left: 50% !important;
      margin-left: -15px;
      margin-top: -10px;
      background-color: rgba(0, 0, 0, 0.45);
      font-size: 35px;
      height: 20px !important;
      line-height: 20px !important;
      margin-top: -10px !important;

      &:focus {
        background-color: rgba(36, 131, 213, 0.9);
      }

      &:active {
        background-color: rgba(36, 131, 213, 0.9);
      }
    }

    .vjs-loading-spinner {
      border-color: rgba(36, 131, 213, 0.8);
    }

    .vjs-control-bar2 {
      background-color: #000000;
      display: flex !important;
      flex-wrap: wrap;
      align-items: center;

      /* LIVE / other items on left */
      .vjs-control.vjs-live-control {
        order: 0 !important;
      }

      /* Push volume and play to the right (volume left of play). Support both legacy and modern class names. */
      .vjs-volume-panel,
      .vjs-volume-menu-button {
        order: 1 !important;
        margin-left: auto !important;
      }

      .vjs-play-control {
        order: 2 !important;
      }
    }

    .vjs-control-bar {
      color: #ffffff;
      font-size: 14px;
      display: flex !important;
      flex-wrap: wrap;
      align-items: center;

      /* LIVE / progress on left; progress takes remaining space */
      .vjs-control.vjs-live-control {
        order: 0 !important;
      }

      .vjs-progress-control.vjs-control {
        order: 0 !important;
        flex: 1 1 auto !important;
        min-width: 4em;
      }

      /* Volume then play/pause on the right (volume left of play) */
      .vjs-volume-panel,
      .vjs-volume-menu-button {
        order: 1 !important;
        margin-left: auto !important;
      }

      .vjs-play-control {
        order: 2 !important;
      }

      .vjs-time-control {
        display: none;
      }

      .vjs-resolution-button {
        order: 3;

        .vjs-resolution-button-label {
          display: block;
          line-height: 30px;
        }
      }

      .vjs-playback-rate {
        .vjs-playback-rate-value {
          font-size: 12px;
          line-height: 2.4;
        }

        order: 4;
      }

      .vjs-fullscreen-control {
        order: 5;
      }
    }

    .vjs-volume-level {
      background-color: #2483d5;
    }

    .vjs-progress-control {
      &:hover {
        .vjs-progress-holder {
          font-size: 1.3px;
        }
      }
    }

    .vjs-menu {
      li {
        padding: 0;
        line-height: 20px;
        font-size: 1.1px;
        font-family: 'PingFang SC', 'Helvetica Neue', 'Hiragino Sans GB', 'Segoe UI', 'Microsoft YaHei', '微软雅黑',
          sans-serif;
      }
    }

    .vjs-time-tooltip {
      border-radius: 0;
      font-size: 10px;
      padding: 0;
      width: 30px;
      height: 15px;
      line-height: 15px;
      top: -30px;
    }

    .vjs-menu-button-popup {
      .vjs-menu {
        width: 50px;
        left: -10px;
      }
    }

    .vjs-menu-button-popup.vjs-volume-menu-button-vertical {
      .vjs-menu {
        left: 0;
      }
    }
  }

  .video-js.vjs-no-flex {
    .vjs-menu-button-inline {
      width: 100px;
    }
  }

  .vjs-menu-button-inline {
    .vjs-menu {
      left: 30px;
    }
  }

  .vjs-paused.vjs-has-started.vjs-custom-skin {
    > .video-js {
      .vjs-big-play-button {
        display: block;
        display: block;
      }
    }
  }

  .video-js.vjs-ended {
    .vjs-big-play-button {
      display: block;
      display: block !important;
      display: block;
    }

    .vjs-loading-spinner {
      display: none;
    }
  }

  .video-js.vjs-paused {
    .vjs-big-play-button {
      display: block;
      display: block;
    }
  }

  .vjs-seeking {
    .vjs-big-play-button {
      display: none !important;
    }
  }

  .vjs-waiting {
    .vjs-big-play-button {
      display: none !important;
    }
  }

  .video-js {
    &:hover {
      .vjs-big-play-button {
        background-color: rgba(36, 131, 213, 0.9);
      }
    }
  }

  .vjs-menu-button-popup.vjs-volume-menu-button-vertical {
    .vjs-menu {
      left: 0;
    }
  }

  .vjs-button,
  .video-js button {
    cursor: pointer;

    &:before {
      font-size: 20px;
    }
  }

  .vjs-custom-update-button {
    cursor: pointer;
    font-family: 'VJS Material Icons', serif;
    font-weight: normal;
    font-style: normal;

    &:before {
      content: '\e923'; // this relates to a specific icon
      font-size: 20px;
      line-height: 1.67;
    }

    &.disabled:before {
      content: '\e075'; // this relates to a specific icon
      font-size: 20px;
      line-height: 1.67;
    }
  }

  .vjs-custom-refresh-button {
    cursor: pointer;
    order: 3 !important;

    &:before {
      content: '\f116'; // vjs replay/refresh icon
      font-family: 'VideoJS', serif;
      font-size: 18px;
      line-height: 1.67;
    }
  }

  .vjs-custom-resolution-button {
    cursor: pointer;

    .vjs-control-text {
      height: 42px;
      width: 42px;
      display: block;
      clip: unset;
      line-height: 42px;
      position: absolute;
      top: 0;
    }
  }

  .vjs-custom-arena-button {
    position: absolute !important;
    right: 10px;
    bottom: 50px;
    cursor: pointer;
    width: 90px !important;
    height: 30px !important;
    padding: 4px 8px !important;
    border-radius: 4px;
    border: solid 1.5px #fff !important;
    background-color: #e4e7eb !important;

    .vjs-control-text {
      clip: unset;
      height: 30px;
      width: 100%;
      display: block;
      color: #1a1d22;
      line-height: 30px;
      top: 0;
      text-transform: uppercase;
      left: 50%;
      transform: translateX(-50%);
    }

    &.active {
      background-color: #08172e !important;

      .vjs-control-text {
        color: #fff;
      }
    }

    &.arena-1 {
      bottom: 50px;
    }

    &.arena-2 {
      bottom: 90px;
    }

    &.arena-3 {
      bottom: 130px;
    }

    &.arena-4 {
      bottom: 170px;
    }

    &.arena-5 {
      bottom: 210px;
    }
  }

  .video-banner-wrapper {
    display: flex;
    justify-content: space-between;
    padding: 0 24px;
    height: 48px;
    box-sizing: border-box;
    align-items: center;
    background-image: url('~@/assets/svg/video-banner.svg');
    background-position: center;
    background-size: cover;
    font-size: 12px;
    font-weight: 600;
    line-height: 40px;
    color: #fafafa;
    text-transform: uppercase;

    &.is-mobile {
      display: none;
    }

    .odds-wrapper {
      display: flex;
      align-items: center;

      .odds {
        font-size: 22px;
        color: #fff;
        text-transform: none;
        user-select: none;
      }
    }

    &.reverse {
      flex-direction: row-reverse;
      background-image: url('~@/assets/svg/video-banner-reverse.svg');
    }
  }

  .vjs-custom-arena-button-temp {
    cursor: pointer;
    width: 90px !important;
    height: 30px !important;
    padding: 4px 8px !important;
    border-radius: 4px;
    border: solid 1.5px #fff !important;
    background-color: #e4e7eb !important;
    margin-right: 8px;

    .vjs-control-text {
      clip: unset;
      height: 30px;
      width: 100%;
      display: block;
      color: #1a1d22;
      line-height: 30px;
      top: 0;
      text-transform: uppercase;
      left: 50%;
      transform: translateX(-50%);
    }

    &.active {
      background-color: #08172e !important;
      color: #fff;
    }
  }

  video {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }

  // Pull-to-refresh indicator (iOS)
  .pull-refresh-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    z-index: 1002;
    pointer-events: none;
  }

  .pull-refresh-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.6);
    color: #ffffff;
    transition: transform 0.15s ease, opacity 0.15s ease;
    margin-top: 8px;

    svg {
      width: 20px;
      height: 20px;
      transition: transform 0.2s ease;
    }

    &.is-ready {
      background-color: rgba(36, 131, 213, 0.8);

      svg {
        transform: rotate(180deg);
      }
    }

    &.is-refreshing {
      opacity: 1 !important;
      transform: translateY(0) !important;
      background-color: rgba(36, 131, 213, 0.9);

      svg {
        animation: pullRefreshSpin 0.8s linear infinite;
      }
    }
  }

  @keyframes pullRefreshSpin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .robust-watermark-overlay {
    position: absolute;
    transform: translate(-50%, -50%);
    pointer-events: none;
    z-index: 999;
    color: #ffffff;
    font-size: clamp(14vw, 18vh, 100px);
    /* moderate preset: opacity 0.15, blurAmount 6 */
    opacity: 0.25;
    filter: blur(6px);
    font-family: Arial, sans-serif;
    font-weight: 600;
    user-select: none;
    white-space: nowrap;
    text-shadow: 0 0 8px rgba(0, 0, 0, 0.5);
    will-change: top, left;
  }

  .video-layers {
    position: absolute;
    color: #ff0000;
    font-size: 30px;
    font-family: Arial, sans-serif;
    font-weight: 500;
    pointer-events: none;
    z-index: 1000;
    white-space: nowrap;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.7);
    user-select: none;

    &.movement-0 {
      transition: top 0.3s ease, left 0.3s ease;
    }

    &.movement-1 {
      will-change: top, left; // Optimize for frequent position changes
    }

    &.movement-2 {
      animation: marqueeScroll 20s linear infinite;
      left: 100%;
      top: 50%;
      transform: translateY(-50%);
    }

    &.movement-3 {
      transition: top 1.5s ease, left 1.5s ease;
      will-change: top, left;
    }
  }

  .video-layers-image {
    max-width: 150px;
    max-height: 150px;
    width: auto;
    height: auto;
    object-fit: contain;
    filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.7));

    &.movement-2 {
      max-width: 200px;
      max-height: 100px;
    }
  }

  // Marquee animation
  @keyframes marqueeScroll {
    0% {
      transform: translateX(0) translateY(-50%);
    }
    100% {
      transform: translateX(-100vw) translateY(-50%);
    }
  }
}
</style>
